import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface ApprovalRow {
  readonly assetApprovalId: string;
  readonly approvalCode: string;
  readonly assetId: string;
  readonly stage: string;
  readonly stageOrder: number;
  readonly decision: string;
  readonly assignedTo: string | null;
  readonly status: string;
}

const STAGES = ['', 'CREATOR', 'REVIEWER', 'BRAND_TEAM', 'APPROVER', 'PUBLISHER', 'DISTRIBUTION'];
const DECISIONS = ['', 'PENDING', 'APPROVED', 'REJECTED', 'SKIPPED'];
const STATUSES = ['', 'ACTIVE', 'COMPLETED', 'CANCELLED'];

/**
 * M36 — Asset Approval Dashboard (§31). Register of asset approval-workflow
 * records (T493) across the 6 segregated-duty stages (CREATOR → REVIEWER →
 * BRAND_TEAM → APPROVER → PUBLISHER → DISTRIBUTION), showing the decision and
 * record status at each stage. Only fully-approved assets may be published
 * (BR-01). Filterable by stage, decision and record status. Tenant isolation
 * is enforced server-side (LAW 4).
 */
export function AssetApprovalDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ApprovalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [stage, setStage] = useState('');
  const [decision, setDecision] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (stage) params.set('stage', stage);
    if (decision) params.set('decision', decision);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ApprovalRow[]; total: number }>(`/v1/dam/approvals${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, stage, decision, status, reload]);

  const decide = async (r: ApprovalRow, verdict: 'APPROVED' | 'REJECTED', reason?: string): Promise<void> => {
    setNotice(null);
    setBusyId(r.assetApprovalId);
    try {
      await apiRequest(`/v1/dam/approvals/${r.assetApprovalId}/decision`, {
        method: 'POST',
        body: verdict === 'REJECTED' ? { decision: verdict, rejectionReason: reason } : { decision: verdict },
        token,
      });
      setNotice(t('m36.common.codeLabel', { code: r.approvalCode, label: verdict === 'APPROVED' ? t('m36.approval.act.approved') : t('m36.approval.act.rejected') }));
      setRejectId(null);
      setRejectReason('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setNotice(t('m36.common.permRequired', { perm: 'M36_ASSET_REVIEW' }));
      else setNotice((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="asset-approval-dashboard">
      <h1>{t('m36.approval.title')}</h1>
      <div className="filters">
        <label>
          {t('m36.common.stage')}
          <select aria-label="filter-stage" value={stage} onChange={(e) => setStage(e.target.value)}>
            {STAGES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.decision')}
          <select aria-label="filter-decision" value={decision} onChange={(e) => setDecision(e.target.value)}>
            {DECISIONS.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status" aria-label="approval-notice">{notice}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="approval-count">{t('m36.approval.count', { count: total })}</p>
          <DataTable<ApprovalRow>
            ariaLabel="approval-table"
            testId="approval-table"
            rowKey={(r) => r.assetApprovalId}
            rowDataAttrs={(r) => ({ 'data-stage': r.stage, 'data-decision': r.decision })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m36.common.code'), render: (r) => r.approvalCode },
              { key: 'asset', header: t('m36.common.asset'), render: (r) => r.assetId },
              { key: 'stage', header: t('m36.common.stage'), render: (r) => r.stage },
              { key: 'order', header: t('m36.approval.col.order'), render: (r) => r.stageOrder },
              { key: 'decision', header: t('m36.common.decision'), render: (r) => r.decision },
              { key: 'status', header: t('m36.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m36.common.actions'), render: (r) => (
                r.decision === 'PENDING' ? (
                  rejectId === r.assetApprovalId ? (
                    <>
                      <input
                        aria-label={`reject-reason-${r.approvalCode}`}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder={t('m36.common.reasonPh')}
                      />
                      <Button
                        variant="danger"
                        type="button"
                        aria-label={`confirm-reject-${r.approvalCode}`}
                        disabled={busyId === r.assetApprovalId || !rejectReason.trim()}
                        onClick={() => void decide(r, 'REJECTED', rejectReason.trim())}
                      >{t('m36.common.confirmReject')}</Button>
                      <Button variant="danger" type="button" aria-label={`cancel-reject-${r.approvalCode}`} onClick={() => { setRejectId(null); setRejectReason(''); }}>{t('common.cancel')}</Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        type="button"
                        aria-label={`approve-${r.approvalCode}`}
                        disabled={busyId === r.assetApprovalId}
                        onClick={() => void decide(r, 'APPROVED')}
                      >{t('m36.common.approve')}</Button>
                      <Button
                        variant="danger"
                        type="button"
                        aria-label={`reject-${r.approvalCode}`}
                        disabled={busyId === r.assetApprovalId}
                        onClick={() => { setRejectId(r.assetApprovalId); setRejectReason(''); setNotice(null); }}
                      >{t('m36.common.reject')}</Button>
                    </>
                  )
                ) : <span aria-label={`decided-${r.approvalCode}`}>—</span>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
